import type { ReactNode } from "react";
import { cn } from "@/lib/utils";

export type BuildSection = "calibrations" | "checks" | "wing-cals" | "metrics";

interface BuildSectionCardsProps {
  calibrationCount: number;
  goldenCount: number;
  toeCheckCount: number;
  signedOffToeCheckCount: number;
  yawCheckCount: number;
  signedOffYawCheckCount: number;
  wingCalCount: number;
  metricsCount: number;
  activeSection: BuildSection;
  onSectionChange: (section: BuildSection) => void;
}

function SectionCard({
  label,
  value,
  unit,
  detail,
  detailClass,
  borderColor,
  active,
  onClick,
}: {
  label: string;
  value: string;
  unit: string;
  detail?: ReactNode;
  detailClass?: string;
  borderColor: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className={cn(
        "bg-card border-l-3 border-b-2 pl-4 pr-5 py-3 text-left transition-all",
        borderColor,
        active
          ? "border-b-primary"
          : "border-b-transparent hover:shadow-[0_1px_4px_rgba(0,0,0,0.04)]",
      )}
    >
      <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground whitespace-nowrap">
        {label}
      </div>
      <div className="flex items-baseline justify-between gap-3 mt-0.5">
        <div className="text-lg font-bold tabular-nums whitespace-nowrap">
          {value}
          <span className="text-xs font-normal text-muted-foreground ml-1">{unit}</span>
        </div>
        {detail && (
          <div className={cn("text-[11px] font-medium tabular-nums whitespace-nowrap", detailClass)}>
            {detail}
          </div>
        )}
      </div>
    </button>
  );
}

export function BuildSectionCards({
  calibrationCount,
  goldenCount,
  toeCheckCount,
  signedOffToeCheckCount,
  yawCheckCount,
  signedOffYawCheckCount,
  wingCalCount,
  metricsCount,
  activeSection,
  onSectionChange,
}: BuildSectionCardsProps) {
  const totalChecks = toeCheckCount + yawCheckCount;
  const totalSignedOff = signedOffToeCheckCount + signedOffYawCheckCount;
  const checksComplete = totalChecks > 0 && totalSignedOff >= totalChecks;
  const completenessBorder = (complete: boolean, started: boolean) =>
    complete ? "border-l-emerald-500" : started ? "border-l-amber-400" : "border-l-border";

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mt-6">
      <SectionCard
        label="Calibrations"
        value={String(calibrationCount)}
        unit={calibrationCount === 1 ? "snapshot" : "snapshots"}
        detail={goldenCount > 0 ? `${goldenCount} golden` : undefined}
        detailClass="text-amber-600 dark:text-amber-400"
        borderColor={completenessBorder(false, goldenCount > 0)}
        active={activeSection === "calibrations"}
        onClick={() => onSectionChange("calibrations")}
      />

      <SectionCard
        label="Checks"
        value={String(totalChecks)}
        unit={totalChecks === 1 ? "check" : "checks"}
        detail={totalSignedOff > 0 ? `${totalSignedOff} signed off` : undefined}
        detailClass="text-emerald-600 dark:text-emerald-400"
        borderColor={completenessBorder(checksComplete, totalChecks > 0)}
        active={activeSection === "checks"}
        onClick={() => onSectionChange("checks")}
      />

      <SectionCard
        label="Wing Cals"
        value={String(wingCalCount)}
        unit={wingCalCount === 1 ? "event" : "events"}
        borderColor={completenessBorder(false, wingCalCount > 0)}
        active={activeSection === "wing-cals"}
        onClick={() => onSectionChange("wing-cals")}
      />

      <SectionCard
        label="Bot Metrics"
        value={String(metricsCount)}
        unit={metricsCount === 1 ? "recording" : "recordings"}
        borderColor={completenessBorder(false, metricsCount > 0)}
        active={activeSection === "metrics"}
        onClick={() => onSectionChange("metrics")}
      />
    </div>
  );
}
